import Flash from 'admin/components/shared/Flash'
import Pagination from 'admin/components/shared/Pagination'
import Tabs from 'admin/components/shared/Tabs'
import useDocument from 'admin/hooks/useDocument'
import useRepeatingInvoices from 'admin/hooks/useRepeatingInvoices'
import {formatDate, formatNumber, pluralise} from 'admin/lib/format'
import React, {Fragment} from 'react'
import {Link, useHistory, useParams} from 'react-router-dom'
import Page from '../shared/Page'
import Filters from './Filters'
import Sorting from '../shared/Sorting'

const sortOptions = [
  {
    sortBy: 'next_invoice_date',
    sortDirection: 'desc',
    sortLabel: 'Next invoice date',
    ascLabel: 'Oldest first',
    descLabel: 'Latest first'
  },
  {
    sortBy: 'next_invoice_due_date',
    sortDirection: 'desc',
    sortLabel: 'Next invoice due date',
    ascLabel: 'Oldest first',
    descLabel: 'Latest first'
  },
  {
    sortBy: 'end_date',
    sortDirection: 'desc',
    sortLabel: 'End date',
    ascLabel: 'Oldest first',
    descLabel: 'Latest first'
  },
  {sortBy: 'account_name', sortDirection: 'asc', sortLabel: 'Account name', ascLabel: 'A-Z', descLabel: 'Z-A'},
  {sortBy: 'amount', sortDirection: 'asc', sortLabel: 'Amount'},
  {
    sortBy: 'frequency',
    sortDirection: 'asc',
    sortLabel: 'Repeat',
    ascLabel: 'Most frequent first',
    descLabel: 'Less frequent first'
  }
]

export default () => {
  const {organisationID} = useParams()
  const history = useHistory()
  const {filters, setFilters, repeatingInvoices, totalCount} = useRepeatingInvoices(organisationID)

  useDocument({title: 'Repeating invoices'})

  const handleRowClick = (e, repeatingInvoice) => {
    if (e.target.tagName.toLowerCase() !== 'a') {
      e.preventDefault()
      history.push(`/orgs/${organisationID}/repeating_invoices/${repeatingInvoice.id}`)
    }
  }

  return (
    <Fragment>
      <Flash />

      <Tabs
        collection={[
          {label: 'Individual', to: `/orgs/${organisationID}/invoices`},
          {label: 'Repeating', to: `/orgs/${organisationID}/repeating_invoices`}
        ]}
      />

      <Page
        id='admin_invoices'
        title='Manage repeating invoices'
        subtitle={
          totalCount
            ? `Showing ${repeatingInvoices.length} of ${pluralise(`repeating invoice`, totalCount, true)}`
            : 'No repeating invoices found'
        }
        buttons={
          <Link to={`/orgs/${organisationID}/repeating_invoices/new`} className='button is-light'>
            New repeating invoice
          </Link>
        }>
        <Filters q={filters.q} fromDate={filters.fromDate} toDate={filters.toDate} onSubmit={setFilters} />
        {repeatingInvoices.length != 0 ? (
          <Fragment>
            <div className='level'>
              <div className='level-left'>
                <Sorting
                  className='level-item'
                  sortOptions={sortOptions}
                  sortBy={filters.sortBy}
                  sortDirection={filters.sortDirection}
                  onChange={setFilters}
                />
              </div>
              <div className='level-right'>
                <Pagination
                  page={filters.page}
                  limit={filters.limit}
                  total={totalCount}
                  onChange={setFilters}
                  className='level-item'
                />
              </div>
            </div>
            <table className='table is-striped is-hoverable is-fullwidth is-linked'>
              <colgroup>
                <col width='40%' />
                <col width='10%' />
                <col width='14.5%' />
                <col width='13%' />
                <col width='12.5%' />
                <col width='10%' />
              </colgroup>
              <thead>
                <tr className='is-size-8'>
                  <th>Account name</th>
                  <th></th>
                  <th>Repeat</th>
                  <th>Next invoice date</th>
                  <th>End date</th>
                  <th className='has-text-right'>Amount</th>
                </tr>
              </thead>
              <tbody>
                {repeatingInvoices.map((repeatingInvoice, i) => (
                  <tr key={i} onClick={e => handleRowClick(e, repeatingInvoice)}>
                    <td>
                      <Link to={`/orgs/${organisationID}/accounts/${repeatingInvoice.account.id}`}>
                        {repeatingInvoice.account.displayName}
                      </Link>
                      {repeatingInvoice.account.displayName !== repeatingInvoice.account.contactFullName &&
                      !!repeatingInvoice.account.contactFullName ? (
                        <sub className='has-text-weight-semibold'>{repeatingInvoice.account.contactFullName}</sub>
                      ) : null}
                    </td>
                    <td className='has-text-right'>
                      {repeatingInvoice.xeroLink && (
                        <span className='tag is-small has-text-white' style={{backgroundColor: '#51afd8'}}>
                          Xero
                        </span>
                      )}
                      {repeatingInvoice.state === 'draft' ? (
                        <span className='tag is-small has-background-grey-light'>Draft</span>
                      ) : repeatingInvoice.isSendable ? (
                        <span className='tag is-success is-light is-small'>Send</span>
                      ) : null}
                    </td>
                    <td>
                      every{' '}
                      {pluralise(
                        repeatingInvoice.frequency?.replace(/ly$/, ''),
                        repeatingInvoice.frequencyCount,
                        repeatingInvoice.frequencyCount > 1
                      )}
                    </td>
                    <td>
                      <span>{formatDate(repeatingInvoice.nextInvoiceDate)}</span>
                      {repeatingInvoice.nextInvoiceDueDate && (
                        <sub className='has-text-weight-semibold'>
                          Due: {formatDate(repeatingInvoice.nextInvoiceDueDate)}
                        </sub>
                      )}
                    </td>
                    <td>{formatDate(repeatingInvoice.endDate)}</td>
                    <td className='has-text-right'>{formatNumber(repeatingInvoice.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {repeatingInvoices.length > 10 && (
              <div className='level'>
                <div className='level-left'></div>
                <div className='level-right'>
                  <Pagination
                    page={filters.page}
                    limit={filters.limit}
                    total={totalCount}
                    onChange={setFilters}
                    className='level-item'
                  />
                </div>
              </div>
            )}
          </Fragment>
        ) : null}
      </Page>
    </Fragment>
  )
}
